"use client";

import * as React from "react";
import { Funnel as Filter } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AddToCartButton, EcommerceProductsProvider, ProductSortDropdown, ProductsFilter, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
declare const wvcClient: any;
const current_wp_query_params = wvcClient.getCurrentWPQueryParams();
const wp_query = new WP_Query(current_wp_query_params);
function ShopProductsConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    total,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  const [selectedProduct, setSelectedProduct] = React.useState<any | null>(null);
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[28rem] border border-border bg-card animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="border-y border-border py-12 text-center font-mono text-sm text-primary">
        UNABLE TO RETRIEVE CATCH.{" "}
        <Button variant="ghost" onClick={refetch} className="h-auto p-0 text-primary underline decoration-primary decoration-2 underline-offset-4 hover:text-foreground hover:bg-transparent">
          RETRY
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border-y border-border py-16 text-center">
        <p className="font-serif text-2xl text-foreground">No catch found in these waters.</p>
        <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
          § ADJUST YOUR FILTERS
        </p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => {
        const isInStock = (product as any).in_stock !== false;
        return <article key={product.id} data-index={i} className="group relative flex flex-col border border-border bg-card p-5 transition-colors duration-300 hover:border-primary">
              <Link to={product.link} className="block">
                <div className="relative aspect-[3/4] overflow-hidden bg-muted">
                  {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]" loading="lazy" decoding="async" />}
                  <span aria-hidden="true" className="pointer-events-none absolute left-2 top-2 h-2 w-2 border-l border-t border-primary opacity-0 transition-opacity duration-[240ms] group-hover:opacity-100" />
                  <span aria-hidden="true" className="pointer-events-none absolute right-2 top-2 h-2 w-2 border-r border-t border-primary opacity-0 transition-opacity duration-[240ms] group-hover:opacity-100" />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-2 left-2 h-2 w-2 border-b border-l border-primary opacity-0 transition-opacity duration-[240ms] group-hover:opacity-100" />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 h-2 w-2 border-b border-r border-primary opacity-0 transition-opacity duration-[240ms] group-hover:opacity-100" />
                </div>
              </Link>

              <div className="mt-4 flex items-center gap-2">
                <span className="inline-block h-2 w-2" style={{
              backgroundColor: isInStock ? "var(--chart-2)" : "var(--destructive)"
            }} aria-hidden="true" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
                  {isInStock ? "AVAILABLE" : "PRE-ORDER"}
                </span>
              </div>

              <Link to={product.link} className="mt-2 block">
                <h3 className="font-serif text-2xl font-normal leading-tight tracking-tight text-foreground transition-all duration-300 group-hover:underline group-hover:decoration-primary group-hover:decoration-[2px] group-hover:underline-offset-[6px]">
                  {product.title}
                </h3>
              </Link>

              <div className="mt-3 flex items-baseline justify-between">
                <span className="font-mono text-base font-medium text-foreground">
                  ${product.price?.toFixed(2)}
                </span>
                <Button variant="ghost" onClick={() => setSelectedProduct(product)} className="h-auto p-0 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground underline decoration-primary decoration-2 underline-offset-4 transition-all hover:decoration-[3px] hover:underline-offset-2 hover:bg-transparent">
                  QUICK VIEW
                  <span style={{
                color: "var(--primary)"
              }}> ●</span>
                </Button>
              </div>

              <div className="mt-4 border-t border-border pt-4">
                <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-secondary text-white rounded-none font-mono uppercase" text="ADD TO CART" showQuantity={false} />
              </div>
            </article>;
      })}
      </div>

      {totalPages > 1 && <div className="mt-16 flex items-center justify-center gap-3 border-t border-border pt-8">
          <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="h-10 font-mono text-xs uppercase tracking-[0.18em] text-foreground hover:bg-transparent hover:text-foreground disabled:opacity-30">
            ← PREV
          </Button>

          <div className="flex items-center gap-2">
            {Array.from({
          length: totalPages
        }).map((_, idx) => {
          const page = idx + 1;
          const isActive = page === currentPage;
          return <Button key={page} data-index={idx} variant="ghost" onClick={() => setPage(page)} className="h-auto gap-2 p-0 font-mono text-xs uppercase tracking-[0.18em] hover:bg-transparent">
                  <span className={isActive ? "text-foreground" : "text-muted-foreground"}>
                    {page.toString().padStart(2, "0")}
                  </span>
                  {page < totalPages && <span aria-hidden="true" className="h-1.5 w-1.5" style={{
              backgroundColor: isActive ? "var(--primary)" : "var(--border)"
            }} />}
                </Button>;
        })}
          </div>

          <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="h-10 font-mono text-xs uppercase tracking-[0.18em] text-foreground hover:bg-transparent hover:text-foreground disabled:opacity-30">
            NEXT →
          </Button>
        </div>}

      <Dialog open={!!selectedProduct} onOpenChange={open => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-4xl border border-border bg-background p-0 sm:max-w-4xl">
          {selectedProduct && <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="aspect-[3/4] overflow-hidden bg-muted md:aspect-auto">
                {selectedProduct.featured_image_url && <img src={selectedProduct.featured_image_url} alt={selectedProduct.title} className="h-full w-full object-cover" loading="lazy" />}
              </div>
              <div className="flex flex-col p-8">
                <DialogHeader className="space-y-3">
                  <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
                    § QUICK VIEW / 30° 24' 42" N · 88° 49' 42" W
                  </p>
                  <DialogTitle className="font-serif text-3xl font-normal leading-tight tracking-tight text-foreground">
                    {selectedProduct.title}
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-4 flex items-baseline gap-3">
                  <span className="font-mono text-2xl font-medium text-foreground">
                    ${selectedProduct.price?.toFixed(2)}
                  </span>
                  <span className="inline-block h-2 w-2" style={{
                backgroundColor: "var(--primary)"
              }} aria-hidden="true" />
                  <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
                    IN STOCK
                  </span>
                </div>

                <div className="my-6 h-px w-full bg-border" />

                {selectedProduct.short_description && <div className="font-default text-sm leading-relaxed text-foreground [&_p]:mb-2 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{
              __html: selectedProduct.short_description
            }} />}

                <div className="mt-auto pt-6">
                  <AddToCartButton productId={selectedProduct.id} className="w-full" buttonClassName="bg-secondary text-white rounded-none font-mono uppercase" text="ADD TO CART" showQuantity={true} />
                  <Link to={selectedProduct.link} className="mt-4 block text-center font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground underline decoration-primary decoration-2 underline-offset-4">
                    VIEW FULL DETAILS
                    <span style={{
                  color: "var(--primary)"
                }}> ●</span>
                  </Link>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </>;
}
function ShopUtilityStrip() {
  const {
    total
  } = useEcommerceProducts();
  return <div className="flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-2" style={{
        backgroundColor: "var(--primary)"
      }} aria-hidden="true" />
        <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
          {total.toString().padStart(2, "0")} PRODUCTS
        </span>
      </div>

      <div className="flex items-center gap-3">
        <span className="hidden font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground sm:inline">
          § SORT
        </span>
      </div>
    </div>;
}
export default function ShopContent() {
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  return <section data-section-id="2066" id="shop-content" className="relative bg-background text-foreground py-20 md:py-28">
      <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8 lg:px-14">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 hidden w-px lg:block" style={{
            left: "calc(25% - 1.5rem)",
            backgroundColor: "var(--border)",
            opacity: 0.3
          }} />

            <aside className="hidden lg:col-span-3 lg:block">
              <div className="sticky top-[120px]">
                <div className="mb-6 flex items-center gap-2">
                  <span className="inline-block h-2 w-2" style={{
                  backgroundColor: "var(--primary)"
                }} aria-hidden="true" />
                  <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
                    § FILTERS / REFINE THE SWELL
                  </p>
                </div>
                <div className="h-px w-full bg-border mb-6" />
                <ProductsFilter />
              </div>
            </aside>

            <div className="lg:hidden">
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="h-12 w-full justify-between border-foreground bg-transparent font-mono text-xs uppercase tracking-[0.18em] text-foreground hover:bg-foreground hover:text-background">
                    <span className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      FILTERS
                    </span>
                    <span className="inline-block h-2 w-2" style={{
                    backgroundColor: "var(--primary)"
                  }} aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-full overflow-y-auto bg-background sm:max-w-md">
                  <SheetHeader>
                    <SheetTitle className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
                      § FILTERS / REFINE THE SWELL
                    </SheetTitle>
                  </SheetHeader>
                  <div className="px-4 pb-8">
                    <ProductsFilter />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <div className="lg:col-span-9">
              <div className="space-y-6">
                <ShopUtilityStrip />
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
                  <span className="hidden font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground sm:inline">
                    SORT
                  </span>
                  <ProductSortDropdown label="" />
                </div>
                <ShopProductsConsumer />
              </div>
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}